import { useCallback, useEffect, useRef, useState } from "react";

export type NoraPresenceState = "idle" | "working" | "waiting";

type Playback = {
  kind: "loop" | "transition";
  source: string;
  target: NoraPresenceState;
};

type TransitionKey = `${NoraPresenceState}:${NoraPresenceState}`;

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: (now: number) => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

const ASSET_ROOT = "/brand/presence/v1";

const STATE_LABELS: Record<NoraPresenceState, string> = {
  idle: "Nora se repose et attend votre demande",
  working: "Nora travaille sur votre demande",
  waiting: "Nora attend votre réponse",
};

const LOOP_CLIPS: Record<NoraPresenceState, string> = {
  idle: `${ASSET_ROOT}/idle-loop.mp4`,
  working: `${ASSET_ROOT}/working-loop.mp4`,
  waiting: `${ASSET_ROOT}/waiting-loop.mp4`,
};

const FALLBACK_IMAGES: Record<NoraPresenceState, string> = {
  idle: `${ASSET_ROOT}/idle.png`,
  working: `${ASSET_ROOT}/working.png`,
  waiting: `${ASSET_ROOT}/waiting.png`,
};

const TRANSITION_CLIPS: Partial<Record<TransitionKey, string>> = {
  "idle:working": `${ASSET_ROOT}/idle-to-working.mp4`,
  "working:idle": `${ASSET_ROOT}/working-to-idle.mp4`,
  "working:waiting": `${ASSET_ROOT}/working-to-waiting.mp4`,
  "waiting:working": `${ASSET_ROOT}/waiting-to-working.mp4`,
};

const VIDEO_CLIPS = [...Object.values(LOOP_CLIPS), ...Object.values(TRANSITION_CLIPS)];

const VERTEX_SHADER = `
attribute vec2 a_position;
attribute vec2 a_texture;
varying vec2 v_texture;

void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_texture = a_texture;
}
`;

const FRAGMENT_SHADER = `
precision mediump float;

uniform sampler2D u_video;
varying vec2 v_texture;

void main() {
  vec4 pixel = texture2D(u_video, v_texture);
  float strongestOtherChannel = max(pixel.r, pixel.b);
  float greenLead = pixel.g - strongestOtherChannel;
  float greenScreen = smoothstep(0.08, 0.30, greenLead) * smoothstep(0.34, 0.72, pixel.g);
  float alpha = 1.0 - greenScreen;

  vec3 color = pixel.rgb;
  float spill = smoothstep(0.01, 0.24, greenLead);
  color.g = mix(color.g, strongestOtherChannel, spill * 0.82);

  if (alpha < 0.01) discard;
  gl_FragColor = vec4(color * alpha, alpha);
}
`;

function loopPlayback(state: NoraPresenceState): Playback {
  return { kind: "loop", source: LOOP_CLIPS[state], target: state };
}

function createShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Création du shader impossible.");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || "Compilation du shader impossible.";
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return reduced;
}

function useChromaKeyRenderer(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  enabled: boolean,
  onReady: () => void,
  onFailure: () => void,
) {
  useEffect(() => {
    if (!enabled) return;
    const video = videoRef.current as VideoWithFrameCallback | null;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const gl = canvas.getContext("webgl", {
      alpha: true,
      antialias: true,
      depth: false,
      stencil: false,
      premultipliedAlpha: true,
      powerPreference: "low-power",
    });
    if (!gl) {
      onFailure();
      return;
    }

    let vertexShader: WebGLShader | null = null;
    let fragmentShader: WebGLShader | null = null;
    let program: WebGLProgram | null = null;
    let buffer: WebGLBuffer | null = null;
    let texture: WebGLTexture | null = null;
    let animationFrame = 0;
    let videoFrame = 0;
    let stopped = false;
    let firstFrameDrawn = false;

    try {
      vertexShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
      fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
      program = gl.createProgram();
      if (!program) throw new Error("Création du programme WebGL impossible.");
      gl.attachShader(program, vertexShader);
      gl.attachShader(program, fragmentShader);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) || "Liaison du programme WebGL impossible.");
      }

      buffer = gl.createBuffer();
      texture = gl.createTexture();
      if (!buffer || !texture) throw new Error("Initialisation WebGL impossible.");

      gl.useProgram(program);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
        -1, -1, 0, 0,
         1, -1, 1, 0,
        -1,  1, 0, 1,
        -1,  1, 0, 1,
         1, -1, 1, 0,
         1,  1, 1, 1,
      ]), gl.STATIC_DRAW);

      const position = gl.getAttribLocation(program, "a_position");
      const textureCoordinate = gl.getAttribLocation(program, "a_texture");
      if (position < 0 || textureCoordinate < 0) throw new Error("Attribut WebGL manquant.");
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 16, 0);
      gl.enableVertexAttribArray(textureCoordinate);
      gl.vertexAttribPointer(textureCoordinate, 2, gl.FLOAT, false, 16, 8);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
      const sampler = gl.getUniformLocation(program, "u_video");
      gl.uniform1i(sampler, 0);
    } catch {
      onFailure();
      return;
    }

    const draw = () => {
      if (stopped || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
      try {
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        if (!firstFrameDrawn) {
          firstFrameDrawn = true;
          onReady();
        }
      } catch {
        stopped = true;
        onFailure();
      }
    };

    const schedule = () => {
      if (stopped) return;
      if (video.requestVideoFrameCallback) {
        videoFrame = video.requestVideoFrameCallback(() => {
          draw();
          schedule();
        });
      } else {
        animationFrame = window.requestAnimationFrame(() => {
          draw();
          schedule();
        });
      }
    };

    const contextLost = (event: Event) => {
      event.preventDefault();
      stopped = true;
      onFailure();
    };
    const drawLoadedFrame = () => draw();
    canvas.addEventListener("webglcontextlost", contextLost);
    video.addEventListener("loadeddata", drawLoadedFrame);
    video.addEventListener("seeked", drawLoadedFrame);
    draw();
    schedule();

    return () => {
      stopped = true;
      canvas.removeEventListener("webglcontextlost", contextLost);
      video.removeEventListener("loadeddata", drawLoadedFrame);
      video.removeEventListener("seeked", drawLoadedFrame);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      if (videoFrame && video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(videoFrame);
      if (texture) gl.deleteTexture(texture);
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
      if (vertexShader) gl.deleteShader(vertexShader);
      if (fragmentShader) gl.deleteShader(fragmentShader);
    };
  }, [canvasRef, enabled, onFailure, onReady, videoRef]);
}

export function NoraPresence({ state }: { state: NoraPresenceState }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reducedMotion = useReducedMotion();
  const desiredState = useRef(state);
  const activeState = useRef(state);
  const [playback, setPlayback] = useState<Playback>(() => loopPlayback(state));
  const playbackRef = useRef(playback);
  const [rendererReady, setRendererReady] = useState(false);
  const [rendererFailed, setRendererFailed] = useState(false);

  const commitPlayback = useCallback((next: Playback) => {
    playbackRef.current = next;
    setPlayback(next);
  }, []);

  const moveToward = useCallback((from: NoraPresenceState, target: NoraPresenceState) => {
    if (from === target || reducedMotion) {
      activeState.current = target;
      commitPlayback(loopPlayback(target));
      return;
    }
    const transition = TRANSITION_CLIPS[`${from}:${target}`];
    if (!transition) {
      activeState.current = target;
      commitPlayback(loopPlayback(target));
      return;
    }
    commitPlayback({ kind: "transition", source: transition, target });
  }, [commitPlayback, reducedMotion]);

  useEffect(() => {
    desiredState.current = state;
    if (reducedMotion) {
      activeState.current = state;
      commitPlayback(loopPlayback(state));
      return;
    }
    if (playbackRef.current.kind !== "transition") moveToward(activeState.current, state);
  }, [commitPlayback, moveToward, reducedMotion, state]);

  useEffect(() => {
    if (reducedMotion || rendererFailed) return;
    const controller = new AbortController();
    for (const source of VIDEO_CLIPS) {
      if (source === playbackRef.current.source) continue;
      void fetch(source, { cache: "force-cache", signal: controller.signal }).catch(() => undefined);
    }
    return () => controller.abort();
  }, [reducedMotion, rendererFailed]);

  useEffect(() => {
    if (reducedMotion || rendererFailed) return;
    const video = videoRef.current;
    if (!video) return;
    video.defaultMuted = true;
    video.muted = true;
    video.loop = playback.kind === "loop";
    video.src = playback.source;
    video.load();
    const play = video.play();
    if (play) {
      void play.catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setRendererFailed(true);
      });
    }
  }, [playback, reducedMotion, rendererFailed]);

  useEffect(() => {
    if (reducedMotion || rendererFailed) setRendererReady(false);
  }, [reducedMotion, rendererFailed]);

  const rendererReadyCallback = useCallback(() => setRendererReady(true), []);
  const rendererFailureCallback = useCallback(() => setRendererFailed(true), []);
  useChromaKeyRenderer(
    videoRef,
    canvasRef,
    !reducedMotion && !rendererFailed,
    rendererReadyCallback,
    rendererFailureCallback,
  );

  const finishTransition = useCallback(() => {
    const current = playbackRef.current;
    if (current.kind !== "transition") return;
    const arrived = current.target;
    activeState.current = arrived;
    moveToward(arrived, desiredState.current);
  }, [moveToward]);

  const renderer = reducedMotion ? "static" : rendererFailed ? "fallback" : "webgl";

  return (
    <div
      className="nora-presence"
      data-state={state}
      data-clip={playback.kind}
      data-source={playback.source}
      data-renderer={renderer}
      data-ready={rendererReady ? "true" : "false"}
      role="img"
      aria-label={STATE_LABELS[state]}
    >
      <span className="nora-presence-stage" aria-hidden="true">
        <img className="nora-presence-fallback" src={FALLBACK_IMAGES[state]} alt="" draggable="false" />
        {!reducedMotion && !rendererFailed ? (
          <>
            <video
              className="nora-presence-video-source"
              ref={videoRef}
              muted
              playsInline
              preload="auto"
              tabIndex={-1}
              onEnded={finishTransition}
              onError={rendererFailureCallback}
            />
            <canvas className="nora-presence-canvas" ref={canvasRef} width="480" height="480" />
          </>
        ) : null}
      </span>
    </div>
  );
}
